import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  ShieldOutlined as ShieldIcon,
  Check as CheckIcon,
  HistoryOutlined as HistoryIcon,
  Brightness4 as MoonIcon,
  Brightness7 as SunIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeModeContext';

/** Purpose: browse platform audit events, optionally embedded in another admin page. Why: privileged changes need a searchable accountability trail. */
export default function PlatformAuditLogs({ embedded = false }) {
  const { isAdmin } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === 'dark';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters & Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [availableActions, setAvailableActions] = useState([]);
  const [tenantsList, setTenantsList] = useState([]);

  // Detail Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [copied, setCopied] = useState(false);

  /** Purpose: Load fetch logs for the platform audit logs module. Why: The selected view needs current data to render or continue processing. */
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit,
      };
      if (search.trim()) params.search = search.trim();
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      if (tenantFilter && tenantFilter !== 'all') params.tenantId = tenantFilter;

      const res = await axios.get('/api/v1/admin/platform/audit-logs', { params });
      if (res.data?.success) {
        setLogs(res.data.auditLogs || []);
        setTotal(res.data.pagination?.total || 0);
        setTotalPages(res.data.pagination?.pages || 1);
        if (Array.isArray(res.data.filters?.availableActions)) {
          setAvailableActions(res.data.filters.availableActions);
        }
        if (Array.isArray(res.data.filters?.tenants)) {
          setTenantsList(res.data.filters.tenants);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load audit trail logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, actionFilter, tenantFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /** Purpose: Handle handle copy json in the platform audit logs module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleCopyJson = (data) => {
    if (!data) return;
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /** Purpose: Handle handle export csv in the platform audit logs module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleExportCsv = () => {
    if (!logs.length) return;
    const headers = ['Timestamp', 'Actor Name', 'Actor Email', 'Actor Role', 'Action Event', 'Entity Type', 'Entity ID', 'IP Address', 'Metadata'];
    /** Purpose: Resolve rows for the platform audit logs module. Why: The surrounding operation uses this value to make its decision. */
    const rows = logs.map((log) => [
      new Date(log.createdAt).toISOString(),
      `"${(log.actorName || '').replace(/"/g, '""')}"`,
      `"${(log.actorEmail || '').replace(/"/g, '""')}"`,
      log.actorRole || '',
      log.action || '',
      log.entityType || '',
      log.entityId || '',
      log.ip || '',
      `"${JSON.stringify(log.metadata || log.after || {}).replace(/"/g, '""')}"`,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `zelorichbot-audit-logs-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /** Purpose: Perform format timestamp for the platform audit logs module. Why: Callers need a consistent result from this shared operation. */
  const formatTimestamp = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  /** Purpose: Perform get action style for the platform audit logs module. Why: Callers need a consistent result from this shared operation. */
  const getActionStyle = (action = '', dark = false) => {
    const act = action.toUpperCase();
    if (act.includes('DELETED')) {
      return dark
        ? { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.35)', text: '#f87171' }
        : { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
    }
    if (act.includes('LOGIN') || act.includes('AUTH')) {
      return dark
        ? { bg: 'rgba(14, 165, 233, 0.15)', border: 'rgba(14, 165, 233, 0.35)', text: '#38bdf8' }
        : { bg: '#f0f9ff', border: '#bae6fd', text: '#0284c7' };
    }
    if (act.includes('CREATED') || act.includes('PROMOTED')) {
      return dark
        ? { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.35)', text: '#34d399' }
        : { bg: '#ecfdf5', border: '#a7f3d0', text: '#059669' };
    }
    if (act.includes('CREDENTIALS') || act.includes('TOKEN') || act.includes('KEY')) {
      return dark
        ? { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.35)', text: '#fbbf24' }
        : { bg: '#fffbeb', border: '#fde68a', text: '#d97706' };
    }
    return dark
      ? { bg: 'rgba(99, 102, 241, 0.15)', border: 'rgba(99, 102, 241, 0.35)', text: '#818cf8' }
      : { bg: '#eef2ff', border: '#c7d2fe', text: '#4f46e5' };
  };

  /** Purpose: Filter stats within the platform audit logs module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const stats = useMemo(() => {
    const deletions = logs.filter((l) => (l.action || '').toUpperCase().includes('DELETED')).length;
    const logins = logs.filter((l) => (l.action || '').toUpperCase().includes('LOGIN')).length;
    return {
      total,
      deletions,
      logins,
      tenants: tenantsList.length,
    };
  }, [logs, total, tenantsList]);

  if (!isAdmin()) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Access denied. Super Admin platform credentials required.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: 'transparent',
        color: isDark ? '#f8fafc' : '#0f172a',
        p: embedded ? 0 : { xs: 2, md: 3.5 },
        transition: 'background-color 0.2s ease, color 0.2s ease',
      }}
    >
      {!embedded && (
        <>
          {/* Top Banner & Appearance Controls */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {/* Super Admin Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '9999px',
                  bgcolor: isDark ? 'rgba(14, 165, 233, 0.12)' : '#e0f2fe',
                  border: isDark ? '1px solid rgba(14, 165, 233, 0.3)' : '1px solid #7dd3fc',
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: '#0284c7',
                    boxShadow: isDark ? '0 0 8px #38bdf8' : 'none',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                    color: isDark ? '#38bdf8' : '#0369a1',
                    fontSize: '0.70rem',
                  }}
                >
                  SUPER ADMIN CONSOLE
                </Typography>
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '9999px',
                  bgcolor: isDark ? 'rgba(245, 158, 11, 0.12)' : '#fef3c7',
                  border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #fde68a',
                  color: isDark ? '#fbbf24' : '#b45309',
                  fontSize: '0.70rem',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                }}
              >
                Platform Mode
              </Box>
            </Box>

            {/* Appearance Theme Switcher Pill (Matching Settings Header in screenshot) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: '9999px',
                bgcolor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
              }}
            >
              <Box sx={{ textAlign: 'left', lineHeight: 1.1 }}>
                <Typography
                  sx={{
                    fontSize: '9px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: isDark ? '#94a3b8' : '#64748b',
                  }}
                >
                  Appearance
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: isDark ? '#cbd5e1' : '#475569',
                  }}
                >
                  Theme
                </Typography>
              </Box>

              <Button
                size="small"
                onClick={toggleMode}
                startIcon={isDark ? <SunIcon sx={{ fontSize: '16px !important' }} /> : <MoonIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  borderRadius: '9999px',
                  px: 2,
                  py: 0.7,
                  bgcolor: '#0f172a',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.80rem',
                  textTransform: 'none',
                  border: isDark ? '1px solid rgba(255, 255, 255, 0.18)' : '1px solid #0f172a',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: '#1e293b',
                  },
                }}
              >
                {isDark ? 'Light mode' : 'Dark mode'}
              </Button>
            </Box>
          </Box>

          {/* Main Title Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: isDark ? '#ffffff' : '#0f172a',
                fontSize: { xs: '1.75rem', md: '2.25rem' },
              }}
            >
              Platform Audit Trail
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? '#94a3b8' : '#64748b',
                mt: 0.5,
                fontSize: { xs: '0.88rem', md: '0.98rem' },
              }}
            >
              Immutable audit logs of all security-critical operations, tenant deletions, logins, and API updates.
            </Typography>
          </Box>
        </>
      )}

      {/* Overview Stat Cards (Clean SaaS Metrics) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Total Logs
            </Typography>
            <HistoryIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
            {total.toLocaleString()}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Deletions
            </Typography>
            <SecurityIcon sx={{ fontSize: 18, color: '#ef4444' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: isDark ? '#f87171' : '#dc2626' }}>
            {stats.deletions}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Auth & Logins
            </Typography>
            <ShieldIcon sx={{ fontSize: 18, color: '#06b6d4' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: isDark ? '#38bdf8' : '#0284c7' }}>
            {stats.logins}
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.03)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Tenants
            </Typography>
            <BusinessIcon sx={{ fontSize: 18, color: '#10b981' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: isDark ? '#34d399' : '#059669' }}>
            {stats.tenants}
          </Typography>
        </Paper>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            bgcolor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
            color: isDark ? '#fca5a5' : '#b91c1c',
            border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fecaca',
            borderRadius: 2.5,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Filter / Search Toolbar (Styled like the Settings card in screenshot) */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          borderRadius: 3.5,
          boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.2)' : '0 4px 16px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', flex: 1, minWidth: 280 }}>
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search actor, action event, entity..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={{
                minWidth: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: isDark ? '#1e293b' : '#f8fafc',
                  color: isDark ? '#f8fafc' : '#0f172a',
                  borderRadius: 2.5,
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                  },
                  '&:hover fieldset': {
                    borderColor: isDark ? '#38bdf8' : '#2563eb',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: isDark ? '#38bdf8' : '#2563eb',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Action Filter Dropdown */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>Action Event</InputLabel>
              <Select
                value={actionFilter}
                label="Action Event"
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                sx={{
                  bgcolor: isDark ? '#1e293b' : '#f8fafc',
                  color: isDark ? '#f8fafc' : '#0f172a',
                  borderRadius: 2.5,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                  },
                  '& .MuiSvgIcon-root': { color: isDark ? '#94a3b8' : '#64748b' },
                }}
              >
                <MenuItem value="all">All Action Events</MenuItem>
                {availableActions.map((act) => (
                  <MenuItem key={act} value={act}>
                    {act}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tenant Filter Dropdown */}
            {tenantsList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>Tenant</InputLabel>
                <Select
                  value={tenantFilter}
                  label="Tenant"
                  onChange={(e) => {
                    setTenantFilter(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    bgcolor: isDark ? '#1e293b' : '#f8fafc',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    borderRadius: 2.5,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                    },
                    '& .MuiSvgIcon-root': { color: isDark ? '#94a3b8' : '#64748b' },
                  }}
                >
                  <MenuItem value="all">All Tenants</MenuItem>
                  {tenantsList.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Tooltip title="Refresh audit log stream">
              <Button
                variant="outlined"
                size="small"
                onClick={fetchLogs}
                disabled={loading}
                startIcon={<RefreshIcon className={loading ? 'animate-spin' : ''} />}
                sx={{
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1',
                  color: isDark ? '#cbd5e1' : '#334155',
                  borderRadius: 2.5,
                  px: 2,
                  py: 0.8,
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: isDark ? 'transparent' : '#ffffff',
                  '&:hover': {
                    borderColor: isDark ? '#38bdf8' : '#2563eb',
                    bgcolor: isDark ? 'rgba(56, 189, 248, 0.08)' : '#f1f5f9',
                  },
                }}
              >
                Refresh
              </Button>
            </Tooltip>

            <Tooltip title="Export current filtered view as CSV">
              <Button
                variant="contained"
                size="small"
                onClick={handleExportCsv}
                disabled={!logs.length}
                startIcon={<DownloadIcon />}
                sx={{
                  bgcolor: '#2563eb',
                  color: '#ffffff',
                  borderRadius: 2.5,
                  px: 2.2,
                  py: 0.8,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                  '&:hover': { bgcolor: '#1d4ed8' },
                }}
              >
                Export CSV
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Main Table Container */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          borderRadius: 3.5,
          overflow: 'hidden',
          boxShadow: isDark ? '0 20px 40px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(15, 23, 42, 0.05)',
        }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: isDark ? '#0e1526' : '#f8fafc',
                borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
              }}
            >
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#475569', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', py: 2 }}>
                TIMESTAMP
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#475569', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', py: 2 }}>
                ACTOR
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#475569', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', py: 2 }}>
                ACTION EVENT
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#475569', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', py: 2 }}>
                ENTITY
              </TableCell>
              <TableCell sx={{ color: isDark ? '#94a3b8' : '#475569', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', py: 2 }}>
                METADATA DETAILS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                  <CircularProgress size={36} sx={{ color: isDark ? '#38bdf8' : '#2563eb' }} />
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 2 }}>
                    Loading platform audit records...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                  <HistoryIcon sx={{ fontSize: 44, color: isDark ? '#475569' : '#cbd5e1', mb: 1.5 }} />
                  <Typography variant="h6" sx={{ color: isDark ? '#f8fafc' : '#0f172a', fontWeight: 800 }}>
                    No audit records found
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', mt: 0.5 }}>
                    Adjust your search query or action filters to view historical activities.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const actionStyle = getActionStyle(log.action, isDark);
                const metadataString = JSON.stringify(log.metadata || log.after || log.before || {});
                const entityDisplay = `${(log.entityType || 'SYSTEM').toUpperCase()} (${(log.entityId || '').slice(-6) || 'root'})`;

                return (
                  <TableRow
                    key={log._id || log.createdAt}
                    hover
                    sx={{
                      borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid #f1f5f9',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(30, 41, 59, 0.5) !important' : '#f8fafc !important',
                      },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    {/* Timestamp */}
                    <TableCell
                      sx={{
                        color: isDark ? '#cbd5e1' : '#334155',
                        fontSize: '0.82rem',
                        fontFamily: 'monospace',
                        py: 2,
                      }}
                    >
                      {formatTimestamp(log.createdAt)}
                    </TableCell>

                    {/* Actor */}
                    <TableCell sx={{ py: 2 }}>
                      <Box>
                        <Typography sx={{ color: isDark ? '#ffffff' : '#0f172a', fontWeight: 700, fontSize: '0.88rem' }}>
                          {log.actorName || log.actorEmail || 'System'}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <span
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 800,
                              letterSpacing: '0.08em',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              backgroundColor:
                                log.actorRole === 'SUPER_ADMIN'
                                  ? (isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe')
                                  : (isDark ? 'rgba(245, 158, 11, 0.12)' : '#fef3c7'),
                              color:
                                log.actorRole === 'SUPER_ADMIN'
                                  ? (isDark ? '#38bdf8' : '#0369a1')
                                  : (isDark ? '#fbbf24' : '#b45309'),
                              border:
                                log.actorRole === 'SUPER_ADMIN'
                                  ? (isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid #7dd3fc')
                                  : (isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #fde68a'),
                              display: 'inline-block',
                            }}
                          >
                            {log.actorRole || 'TENANT_USER'}
                          </span>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Action Event Chip */}
                    <TableCell sx={{ py: 2 }}>
                      <span
                        style={{
                          fontSize: '0.74rem',
                          fontFamily: 'monospace',
                          fontWeight: 800,
                          padding: '4px 10px',
                          borderRadius: '8px',
                          backgroundColor: actionStyle.bg,
                          color: actionStyle.text,
                          border: `1px solid ${actionStyle.border}`,
                          display: 'inline-block',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {log.action}
                      </span>
                    </TableCell>

                    {/* Entity */}
                    <TableCell
                      sx={{
                        color: isDark ? '#94a3b8' : '#64748b',
                        fontSize: '0.82rem',
                        fontFamily: 'monospace',
                        py: 2,
                      }}
                    >
                      {entityDisplay}
                    </TableCell>

                    {/* Metadata Details */}
                    <TableCell sx={{ py: 2, maxWidth: 360 }}>
                      <Tooltip title="Click to view full JSON payload" arrow>
                        <Box
                          onClick={() => setSelectedLog(log)}
                          sx={{
                            color: isDark ? '#94a3b8' : '#475569',
                            fontSize: '0.80rem',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            p: 0.8,
                            borderRadius: 2,
                            bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#f1f5f9',
                            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              color: isDark ? '#38bdf8' : '#0284c7',
                              borderColor: isDark ? 'rgba(56, 189, 248, 0.5)' : '#7dd3fc',
                              bgcolor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#e0f2fe',
                            },
                          }}
                        >
                          {metadataString.length > 55 ? metadataString.slice(0, 55) + '...' : metadataString}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            bgcolor: isDark ? '#0e1526' : '#f8fafc',
            borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
          }}
        >
          <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.82rem' }}>
            Showing {logs.length ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} audit records
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{
                color: isDark ? '#cbd5e1' : '#334155',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                '&:disabled': { color: isDark ? '#475569' : '#cbd5e1' },
              }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: isDark ? '#f8fafc' : '#0f172a', px: 1, fontWeight: 700, fontSize: '0.82rem' }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                color: isDark ? '#cbd5e1' : '#334155',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                '&:disabled': { color: isDark ? '#475569' : '#cbd5e1' },
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Detail Modal Dialog */}
      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            color: isDark ? '#f8fafc' : '#0f172a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e2e8f0',
            borderRadius: 3.5,
            boxShadow: isDark ? '0 25px 50px -12px rgba(0, 0, 0, 0.8)' : '0 20px 40px rgba(15, 23, 42, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ShieldIcon sx={{ color: isDark ? '#38bdf8' : '#2563eb' }} />
            <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? '#ffffff' : '#0f172a' }}>
              Audit Event Details
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setSelectedLog(null)} sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2.5 }}>
          {selectedLog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Event & Actor Summary Cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: isDark ? '#1e293b' : '#f8fafc',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                    Action Event & Entity
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: isDark ? '#38bdf8' : '#2563eb', mt: 0.5, fontFamily: 'monospace' }}>
                    {selectedLog.action}
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#cbd5e1' : '#334155', mt: 0.5, fontFamily: 'monospace' }}>
                    Entity: {selectedLog.entityType} ({selectedLog.entityId || 'root'})
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#64748b' : '#94a3b8', display: 'block', mt: 0.5 }}>
                    Timestamp: {new Date(selectedLog.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: isDark ? '#1e293b' : '#f8fafc',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.06)' : '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>
                    Actor & Source
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a', mt: 0.5 }}>
                    {selectedLog.actorName || 'System'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                    Email: {selectedLog.actorEmail || 'N/A'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDark ? '#fbbf24' : '#b45309', fontWeight: 700, display: 'block', mt: 0.5 }}>
                    Role: {selectedLog.actorRole} · IP: {selectedLog.ip || 'Unknown'}
                  </Typography>
                </Box>
              </Box>

              {/* Full JSON Payload */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDark ? '#cbd5e1' : '#334155' }}>
                    Payload & Metadata (JSON)
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleCopyJson(selectedLog.metadata || selectedLog.after || selectedLog)}
                    startIcon={copied ? <CheckIcon sx={{ color: '#10b981' }} /> : <ContentCopyIcon />}
                    sx={{
                      color: copied ? '#10b981' : isDark ? '#94a3b8' : '#64748b',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      '&:hover': { color: isDark ? '#ffffff' : '#0f172a' },
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy JSON'}
                  </Button>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: isDark ? '#070a13' : '#f8fafc',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                    borderRadius: 2.5,
                    maxHeight: 280,
                    overflow: 'auto',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: 'monospace',
                      fontSize: '0.80rem',
                      color: isDark ? '#a5f3fc' : '#0369a1',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {JSON.stringify(selectedLog.metadata || selectedLog.after || selectedLog, null, 2)}
                  </pre>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setSelectedLog(null)}
            sx={{
              color: isDark ? '#cbd5e1' : '#475569',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
